import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tracesApiService } from '@/services/api/traces.service';
import type { LangfuseTrace } from '@/types/traces';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Clock, DollarSign, Zap, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConversationView } from '@/components/client/ConversationView';
import { generateMockTracesResponse, getMockTraceById } from '@/services/mock/traces.mock';

interface AdminClientTracesTableProps {
  vapiTag: string;
}

export function AdminClientTracesTable({ vapiTag }: AdminClientTracesTableProps) {
  const [page, setPage] = useState(1);
  const limit = 20;
  const [selectedTrace, setSelectedTrace] = useState<LangfuseTrace | null>(null);
  const [showTraceDetail, setShowTraceDetail] = useState(false);
  const [useMockData, setUseMockData] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-traces', vapiTag, page],
    queryFn: async () => {
      try {
        // Intentar obtener datos reales del API
        const response = await tracesApiService.listTraces({
          tags: [vapiTag],
          page,
          limit
        });
        setUseMockData(false);
        return response;
      } catch (err) {
        // Si falla (503 o cualquier error), usar mock data
        console.warn('API de traces no disponible, usando mock data:', err);
        setUseMockData(true);
        return generateMockTracesResponse(page, limit);
      }
    },
    staleTime: 60 * 1000, // 1 minute
    retry: false, // No reintentar si falla, usar mock inmediatamente
  });

  // Fetch del detalle del trace seleccionado
  const { data: traceDetailData, isLoading: traceDetailLoading } = useQuery({
    queryKey: ['trace-detail', selectedTrace?.id],
    queryFn: async () => {
      if (!selectedTrace) return null;

      if (useMockData) {
        // Usar mock data
        const mockTrace = getMockTraceById(selectedTrace.id);
        return mockTrace ? { success: true, data: mockTrace } : null;
      }

      // Intentar obtener del API
      try {
        return await tracesApiService.getTrace(selectedTrace.id);
      } catch (err) {
        // Fallback a mock data
        const mockTrace = getMockTraceById(selectedTrace.id);
        return mockTrace ? { success: true, data: mockTrace } : null;
      }
    },
    enabled: !!selectedTrace && showTraceDetail,
    retry: false,
  });

  const traces = data?.data || [];
  const totalPages = data?.meta.totalPages || 1;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCost = (cost: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4
    }).format(cost);
  };

  const formatLatency = (latency: number) => {
    if (latency < 1000) {
      return `${Math.round(latency)}ms`;
    }
    return `${(latency / 1000).toFixed(2)}s`;
  };

  const handleViewTrace = (trace: LangfuseTrace) => {
    setSelectedTrace(trace);
    setShowTraceDetail(true);
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar traces: {(error as Error).message}
      </div>
    );
  }

  if (traces.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay traces para este cliente
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mock Data Indicator */}
      {useMockData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
          <strong>Modo Demo:</strong> Mostrando datos de ejemplo. La API de Langfuse no está disponible.
        </div>
      )}

      {/* Traces List */}
      <div className="space-y-2">
        {traces.map((trace: LangfuseTrace) => (
          <div
            key={trace.id}
            className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium">{trace.name || 'Sin nombre'}</span>
                  {trace.success !== undefined && (
                    <Badge variant={trace.success ? 'default' : 'destructive'}>
                      {trace.success ? 'Exitosa' : 'Fallida'}
                    </Badge>
                  )}
                </div>
                {trace.metadata?.assistant_name && (
                  <div className="text-sm text-muted-foreground">
                    Asistente: {trace.metadata.assistant_name}
                  </div>
                )}
              </div>
              <div className="text-right text-sm space-y-1">
                <div className="flex items-center gap-1 justify-end text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  {formatLatency(trace.latency)}
                </div>
                {trace.totalCost > 0 && (
                  <div className="flex items-center gap-1 justify-end text-green-600 font-medium">
                    <DollarSign className="w-3 h-3" />
                    {formatCost(trace.totalCost)}
                  </div>
                )}
              </div>
            </div>

            {/* Tags */}
            {trace.tags && trace.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {trace.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Metadata and Actions */}
            <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
              <div className="flex items-center gap-4">
                <div>ID: {trace.id.slice(0, 8)}...</div>
                {trace.sessionId && (
                  <div>Sesión: {trace.sessionId.slice(0, 8)}...</div>
                )}
                {trace.environment && (
                  <div className="capitalize">{trace.environment}</div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span>{formatDate(trace.timestamp)}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewTrace(trace)}
                  className="h-7 text-xs"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Ver detalle
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Página {page} de {totalPages} • {data?.meta.totalItems || 0} traces
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Trace Detail Dialog */}
      <Dialog open={showTraceDetail} onOpenChange={setShowTraceDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Detalle de Llamada: {selectedTrace?.name || selectedTrace?.id}
            </DialogTitle>
            <DialogDescription>
              Información completa de la llamada
            </DialogDescription>
          </DialogHeader>

          {traceDetailLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner />
            </div>
          ) : traceDetailData?.data ? (
            <Tabs defaultValue="conversation" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="conversation">Conversación</TabsTrigger>
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="metadata">Metadata</TabsTrigger>
              </TabsList>

              <TabsContent value="conversation" className="max-h-[500px] overflow-y-auto pb-6">
                <div className="pb-8">
                  <ConversationView trace={traceDetailData.data} />
                </div>
              </TabsContent>

              <TabsContent value="info" className="space-y-4 max-h-[400px] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Nombre</p>
                    <p>{traceDetailData.data.name || 'Sin nombre'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Asistente</p>
                    <p>{traceDetailData.data.metadata?.assistant_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Timestamp</p>
                    <p>{formatDateTime(traceDetailData.data.timestamp)}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Ambiente</p>
                    <Badge>{traceDetailData.data.environment}</Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Usuario ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.userId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Sesión ID</p>
                    <p className="font-mono text-sm">{traceDetailData.data.sessionId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Duración</p>
                    <p>{traceDetailData.data.latency != null ? formatLatency(traceDetailData.data.latency) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Costo</p>
                    <p>{traceDetailData.data.totalCost != null ? formatCost(traceDetailData.data.totalCost) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Estado</p>
                    {traceDetailData.data.success !== undefined && (
                      <Badge variant={traceDetailData.data.success ? 'default' : 'destructive'}>
                        {traceDetailData.data.success ? 'Exitosa' : 'Fallida'}
                      </Badge>
                    )}
                  </div>
                </div>

                {traceDetailData.data.tags && traceDetailData.data.tags.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {traceDetailData.data.tags.map(tag => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="metadata" className="space-y-4 max-h-[400px] overflow-y-auto">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Metadata</h3>
                  <pre className="bg-gray-50 p-4 rounded text-sm border max-h-[150px] overflow-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(traceDetailData.data.metadata, null, 2)}
                  </pre>
                </div>

                {traceDetailData.data.scores && traceDetailData.data.scores.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Scores</h3>
                    <pre className="bg-gray-50 p-4 rounded text-sm border max-h-[150px] overflow-auto whitespace-pre-wrap break-words">
                      {JSON.stringify(traceDetailData.data.scores, null, 2)}
                    </pre>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <p className="text-red-500 mb-2">Error al cargar los detalles</p>
                <p className="text-sm text-gray-600">No se pudo obtener la información de la llamada</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
